/**
 * The Library's remembered filters (0.91.0): what the server preference `library.filters` holds, and how it
 * is read back. Reading is defensive: the stored value may come from an older PRISM, another browser, or a
 * model that has changed since, so unknown keys and malformed values are ignored rather than trusted.
 * Search text and advanced search are not part of it (they live in the address and in this browser).
 */
import { ADDED_FILTERS, AVAILABILITY_FILTERS, MASTER_TAG, METADATA_FILTERS, QC_FILTERS, RIGHTS_FILTERS, type MetadataSchemaDefinition } from '@prism/contracts';
import type { LibraryFilters } from '../../api/hooks';

export const FILTERS_PREF_KEY = 'library.filters';

/** The filters the pane sets (everything but the search text and the sort). */
export type PaneFilters = Omit<LibraryFilters, 'q' | 'sort' | 'advanced' | 'collection'>;

export const EMPTY_PANE_FILTERS: PaneFilters = {
  status: [], kind: [], location: [], category: [], fields: {}, includeDismissed: false, added: null, metadata: null, availability: null, qc: null, masters: [], rights: null,
};

/** The stored shape. `v` versions it; a reader ignores what it does not know. */
export interface StoredFilters {
  v: 1;
  status?: string[];
  kind?: string[];
  location?: string[];
  category?: string[];
  fields?: Record<string, string[]>;
  includeDismissed?: boolean;
  /** 1.6.0: moments without pending AI suggestions. */
  acceptedOnly?: boolean;
  added?: string | null;
  metadata?: string | null;
  availability?: string | null;
  qc?: string | null;
  /** 1.2.0: Has master tags. */
  masters?: string[];
  /** 1.8.0: the Rights quick filter. */
  rights?: string | null;
}

const STATUSES = new Set(['ready', 'processing', 'registered', 'recording', 'partial', 'failed', 'placeholder']);
const KINDS = new Set(['video', 'audio', 'image', 'graphic']);
const FIELD_ID = /^f_[a-z0-9_]+$/;
const MAX_VALUES = 200;

/** A list of short strings, or [] (with `allowed`, only those values). */
function strings(v: unknown, allowed?: Set<string>): string[] {
  if (!Array.isArray(v)) return [];
  const out = v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 500 && (!allowed || allowed.has(x)));
  return [...new Set(out)].slice(0, MAX_VALUES);
}

function oneOf<T extends string>(v: unknown, options: readonly T[]): T | null {
  return typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : null;
}

/** What is kept on the server: only the parts that are set. */
export function serializeFilters(f: PaneFilters): StoredFilters {
  const out: StoredFilters = { v: 1 };
  if (f.status.length) out.status = f.status;
  if (f.kind.length) out.kind = f.kind;
  if (f.location.length) out.location = f.location;
  if (f.category.length) out.category = f.category;
  const fields = Object.fromEntries(Object.entries(f.fields).filter(([, values]) => values.length));
  if (Object.keys(fields).length) out.fields = fields;
  if (f.includeDismissed) out.includeDismissed = true;
  if (f.acceptedOnly) out.acceptedOnly = true;
  if (f.added) out.added = f.added;
  if (f.metadata) out.metadata = f.metadata;
  if (f.availability) out.availability = f.availability;
  if (f.qc) out.qc = f.qc;
  if (f.masters?.length) out.masters = f.masters;
  if (f.rights) out.rights = f.rights;
  return out;
}

/** The stored value as filters; anything missing, unknown or malformed reads as "no filter". */
export function parseStoredFilters(raw: unknown): PaneFilters {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...EMPTY_PANE_FILTERS, fields: {} };
  const r = raw as Record<string, unknown>;
  const fields: Record<string, string[]> = {};
  if (r.fields && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    for (const [id, values] of Object.entries(r.fields as Record<string, unknown>)) {
      const list = strings(values);
      if (FIELD_ID.test(id) && list.length) fields[id] = list;
    }
  }
  return {
    status: strings(r.status, STATUSES),
    kind: strings(r.kind, KINDS),
    location: strings(r.location),
    category: strings(r.category),
    fields,
    includeDismissed: r.includeDismissed === true,
    ...(r.acceptedOnly === true ? { acceptedOnly: true } : {}),
    added: oneOf(r.added, ADDED_FILTERS),
    metadata: oneOf(r.metadata, METADATA_FILTERS),
    availability: oneOf(r.availability, AVAILABILITY_FILTERS),
    qc: oneOf(r.qc, QC_FILTERS),
    masters: strings(r.masters).filter((t) => MASTER_TAG.test(t)).slice(0, 20),
    rights: oneOf(r.rights, RIGHTS_FILTERS),
  };
}

/**
 * Only metadata fields that are still facets the person can see may be filtered on (the server refuses the
 * others). Values of those fields are kept even when no asset has them any more: they show as chips that
 * can be cleared. Without a schema yet, every field filter waits.
 */
export function usableFieldFilters(fields: Record<string, string[]>, schema: MetadataSchemaDefinition | undefined): Record<string, string[]> {
  if (!schema) return {};
  const facets = new Set(schema.fields.filter((f) => f.facet && FIELD_ID.test(f.id)).map((f) => f.id));
  return Object.fromEntries(Object.entries(fields).filter(([id, values]) => facets.has(id) && values.length));
}

/** Whether any pane filter is set (the Clear filters button shows then). */
export function hasPaneFilters(f: PaneFilters): boolean {
  return Boolean(f.added || f.metadata || f.availability || f.qc || f.rights || f.includeDismissed
 || f.acceptedOnly || f.status.length || f.kind.length || f.location.length || f.category.length || f.masters?.length
    || Object.values(f.fields).some((v) => v.length));
}

/** Two filter states hold the same selections (to avoid writing the preference when nothing changed). */
export function sameFilters(a: PaneFilters, b: PaneFilters): boolean {
  return JSON.stringify(serializeFilters(a)) === JSON.stringify(serializeFilters(b));
}
